import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ArrowLeft, AlertTriangle, AlertCircle, CheckCircle2, ChevronDown, ChevronRight } from 'lucide-react';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

const TIER_LABEL: Record<string, string> = {
  showcase: 'Showcase',
  club_priority: 'Club Priority',
  priority: 'Priority',
  standard: 'Standard',
};

const TIER_TONE: Record<string, string> = {
  showcase: 'bg-purple-100 text-purple-900',
  club_priority: 'bg-blue-100 text-blue-900',
  priority: 'bg-emerald-100 text-emerald-900',
  standard: 'bg-slate-100 text-slate-700',
};

export default function OperatorEventConflictAnalyzerPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<any>(null);
  const [expandedTeams, setExpandedTeams] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!eventId) return;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { data: res, error: fnError } = await invokeEdgeFunction(
          'rle-operator-conflict-analyzer',
          { body: { event_id: eventId } }
        );
        if (fnError || !res?.ok) {
          setError(res?.error?.message || fnError?.message || 'Could not analyze constraints.');
        } else {
          setData(res.data);
          // Auto-expand teams with conflicts
          const auto = new Set<string>();
          for (const t of (res.data.by_team ?? [])) {
            if (t.has_conflicts) auto.add(t.team_id);
          }
          setExpandedTeams(auto);
        }
      } catch (e: any) {
        setError(e?.message ?? 'Unexpected error');
      } finally {
        setLoading(false);
      }
    })();
  }, [eventId]);

  const toggleTeam = (teamId: string) => {
    setExpandedTeams(prev => {
      const next = new Set(prev);
      if (next.has(teamId)) next.delete(teamId);
      else next.add(teamId);
      return next;
    });
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto p-6 space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-rose-900 flex items-center gap-2">
          <AlertCircle className="h-5 w-5" />
          {error}
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { event, summary, conflicts, by_team } = data;
  const hasConflicts = (conflicts?.length ?? 0) > 0;

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div>
        <button
          onClick={() => navigate(`/admin-dashboard/events/${eventId}`)}
          className="flex items-center text-sm text-slate-600 hover:text-slate-900 mb-2"
        >
          <ArrowLeft className="h-4 w-4 mr-1" /> Event overview
        </button>
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Conflict analyzer</h1>
            <p className="text-sm text-slate-600">{event.name}</p>
          </div>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-slate-500 mb-1">Total constraints</p>
            <p className="text-2xl font-bold text-slate-900">{summary.total_constraints}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-1">
              <p className="text-sm text-slate-500">Errors</p>
              {summary.errors_count > 0 && <AlertTriangle className="h-4 w-4 text-rose-600" />}
            </div>
            <p className={`text-2xl font-bold ${summary.errors_count > 0 ? 'text-rose-700' : 'text-slate-900'}`}>{summary.errors_count}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-1">
              <p className="text-sm text-slate-500">Warnings</p>
              {summary.warnings_count > 0 && <AlertTriangle className="h-4 w-4 text-amber-600" />}
            </div>
            <p className={`text-2xl font-bold ${summary.warnings_count > 0 ? 'text-amber-700' : 'text-slate-900'}`}>{summary.warnings_count}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-slate-500 mb-1">Teams affected</p>
            <p className="text-2xl font-bold text-slate-900">
              {by_team.filter((t: any) => t.has_conflicts).length}
            </p>
            <p className="text-xs text-slate-500 mt-1">of {by_team.length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Conflicts section */}
      <Card>
        <CardContent className="p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Detected conflicts</h2>
          {!hasConflicts ? (
            <div className="flex items-center gap-2 text-emerald-700 bg-emerald-50 rounded-lg p-4">
              <CheckCircle2 className="h-5 w-5" />
              No conflicts detected. Schedule generation should be unblocked.
            </div>
          ) : (
            <div className="space-y-3">
              {conflicts.map((c: any, idx: number) => (
                <div
                  key={idx}
                  className="flex items-start gap-3 rounded-lg border p-3"
                >
                  <div className="mt-0.5">
                    {c.severity === 'error'
                      ? <AlertTriangle className="h-5 w-5 text-rose-600" />
                      : <AlertCircle className="h-5 w-5 text-amber-600" />
                    }
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">{c.team_name}</p>
                    <p className="text-sm text-slate-600">{c.message}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* All constraints by team */}
      <Card>
        <CardContent className="p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">All constraints by team</h2>
          {by_team.length === 0 ? (
            <p className="text-sm text-slate-500">No constraints submitted yet for this event.</p>
          ) : (
            <div className="space-y-2">
              {by_team.map((t: any) => {
                const expanded = expandedTeams.has(t.team_id);
                return (
                  <div key={t.team_id} className="rounded-lg border overflow-hidden">
                    <button
                      onClick={() => toggleTeam(t.team_id)}
                      className="w-full text-left flex items-center justify-between gap-3 p-3 hover:bg-slate-50 transition"
                    >
                      <div className="flex items-center gap-3">
                        {expanded ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
                        <span className="font-medium text-slate-900">{t.team_name}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs ${TIER_TONE[t.tier] ?? 'bg-slate-100 text-slate-700'}`}>
                          {TIER_LABEL[t.tier] ?? t.tier}
                        </span>
                        {t.division && <span className="text-xs text-slate-500">{t.division}</span>}
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        {t.has_conflicts && (
                          <Badge variant="destructive" className="text-xs">
                            {t.conflict_count} issue{t.conflict_count === 1 ? '' : 's'}
                          </Badge>
                        )}
                        <span className="text-slate-500">{t.constraints.length} request{t.constraints.length === 1 ? '' : 's'}</span>
                      </div>
                    </button>
                    {expanded && (
                      <div className="px-3 pb-3 space-y-2">
                        {t.constraints.map((c: any, cidx: number) => (
                          <div key={cidx} className="flex items-center justify-between rounded bg-slate-50 p-2 text-sm">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-slate-800 capitalize">{c.type.replace(/_/g, ' ')}</span>
                              <span className="text-slate-500">— {c.formatted}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              {c.has_conflict && <AlertTriangle className="h-4 w-4 text-rose-600" />}
                              <span className={`px-2 py-0.5 rounded-full text-xs ${c.status === 'approved' ? 'bg-emerald-100 text-emerald-900' : c.status === 'denied' ? 'bg-rose-100 text-rose-900' : 'bg-amber-100 text-amber-900'}`}>
                                {c.status.replace(/_/g, ' ')}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
